import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { Link } from "react-router-dom";
import { LogIn, UserPlus, Fingerprint } from "lucide-react";
import { getPasskey, passkeysSupported, friendlyWebAuthnError } from "@/lib/webauthn";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TwoFactorStep } from "@/components/auth/TwoFactorStep";
import EmailSuggestion from "@/components/auth/EmailSuggestion";
import { track } from "@/lib/analytics";

const BG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/016a4bd2-af1d-43ad-aea9-27f10fcd49a6/images/34c0dccc2b64c2bf979052400651d0bebc8969748f06953b590236e2f6876517.png";

function MmntmLogo({ size = 48 }) {
  const id = `ml${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-label="MMNTM logo">
      {/* Container background */}
      <rect x="1" y="1" width="62" height="62" rx="12" fill={`url(#${id}Bg)`} />
      <rect x="1.5" y="1.5" width="61" height="61" rx="11.5" stroke="rgba(168,85,247,0.2)" strokeWidth="1" fill="none" />

      {/* Layer 1: Full M silhouette — filled polygon, metallic gradient */}
      <polygon
        points="12,50 12,14 21,14 32,34 43,14 52,14 52,50 43,50 43,26 32,46 21,26 21,50"
        fill={`url(#${id}Metal)`} />

      {/* Layer 3: Metallic bevel — top-facing edges highlight */}
      <polyline points="12,14 21,14 32,34 43,14 52,14" fill="none"
        stroke="#6A6A75" strokeWidth="1" strokeOpacity="0.5" strokeLinejoin="miter" />
      <line x1="12" y1="14" x2="12" y2="50" stroke="#5A5A65" strokeWidth="0.7" strokeOpacity="0.3" />
      <line x1="52" y1="14" x2="52" y2="50" stroke="#5A5A65" strokeWidth="0.7" strokeOpacity="0.3" />

      {/* Layer 2: Purple V accent — nested in center valley */}
      <polygon
        points="25,34 32,46 39,34 35,34 32,40 29,34"
        fill={`url(#${id}Purple)`} />

      <defs>
        <linearGradient id={`${id}Bg`} x1="0" y1="0" x2="64" y2="64">
          <stop stopColor="#1A1A1F" /><stop offset="1" stopColor="#0A0A0D" />
        </linearGradient>
        <linearGradient id={`${id}Metal`} x1="12" y1="14" x2="52" y2="50" gradientUnits="userSpaceOnUse">
          <stop stopColor="#3D3D47" /><stop offset="0.5" stopColor="#2A2A32" /><stop offset="1" stopColor="#14141A" />
        </linearGradient>
        <linearGradient id={`${id}Purple`} x1="32" y1="34" x2="32" y2="46" gradientUnits="userSpaceOnUse">
          <stop stopColor="#A855F7" /><stop offset="1" stopColor="#6B21A8" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function AuthPage() {
  const { login, register, verifyTwoFactor, finishLogin } = useAuth();
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const signInWithPasskey = async () => {
    setPasskeyBusy(true); setError("");
    try {
      const options = await api.passkeyLoginOptions(email.trim() || undefined);
      const credential = await getPasskey(options);
      const data = await api.passkeyLoginVerify(credential);
      track("passkey_login");
      finishLogin(data);
    } catch (err) { setError(friendlyWebAuthnError(err)); }
    setPasskeyBusy(false);
  };
  const [challenge, setChallenge] = useState(null);
  const [isLogin, setIsLogin] = useState(() => {
    // Invite links can force the signup view via `?signup=1` so users
    // landing from InviteAcceptPage's "Create account" CTA don't have
    // to flip the toggle manually.
    try {
      return new URLSearchParams(window.location.search).get("signup") !== "1";
    } catch {
      return true;
    }
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [emailHint, setEmailHint] = useState(null);

  // Bundle E-4: a 422 with `confirmable` means "likely typo / unknown mail server" — offer a choice, not an error.
  const submitSignup = async (address, confirmEmail = false) => {
    setSubmitting(true);
    const result = await register(address, password, name, referralCode, confirmEmail);
    setSubmitting(false);
    if (result.confirmable) {
      track("signup_email_suggestion_shown", { has_suggestion: !!result.suggestion, reason: result.reason });
      setEmailHint({ suggestion: result.suggestion, message: result.error });
      return result;
    }
    setEmailHint(null);
    return result;
  };
  const finishSignup = async (r) => {
    if (r.success) await acceptPendingInvite();
    else if (!r.confirmable) setError(r.error);
  };
  const [submitting, setSubmitting] = useState(false);
  const [agreeLegal, setAgreeLegal] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const referralCode = urlParams.get("ref") || "";
  // Invite token carried through from an /invites/:token link when the
  // invitee wasn't signed in. After a successful auth we auto-accept
  // and land them on the project they were invited to — no extra click
  // required.
  const inviteToken = urlParams.get("invite") || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && !agreeLegal) {
      setError("Please agree to the Terms of Service and Privacy Policy.");
      return;
    }
    let result;
    if (isLogin) {
      setSubmitting(true);
      result = await login(email, password);
      setSubmitting(false);
    } else {
      result = await submitSignup(email);
      if (result.confirmable) return;
    }
    if (!result.success) { setError(result.error); return; }
    // Bundle S-1: 2FA accounts verify a code before the session exists.
    if (result.twoFactorRequired) { setChallenge(result.challenge); return; }
    await acceptPendingInvite();
  };

  const handleVerifyCode = async (code) => {
    setError("");
    setSubmitting(true);
    const result = await verifyTwoFactor(challenge, code);
    setSubmitting(false);
    if (!result.success) {
      if (result.status === 401 && /no longer valid/i.test(result.error || "")) { setChallenge(null); setPassword(""); }
      setError(result.error);
      return;
    }
    await acceptPendingInvite();
  };

  const acceptPendingInvite = async () => {
    // Auto-accept a pending invite if one was attached to the URL.
    if (inviteToken) {
      try {
        const acceptResult = await api.acceptInviteByToken(inviteToken);
        toast.success("Invitation accepted — welcome to the team!");
        // Gentle delay so the toast renders before navigation.
        setTimeout(() => {
          window.location.href = `/?project=${acceptResult.project_id}`;
        }, 600);
      } catch (err) {
        // Don't block the user — they're already signed in. Show a
        // soft warning and let AuthContext's default /dashboard route
        // handle the redirect.
        toast.error(
          err?.response?.data?.detail || "Signed in, but we couldn't accept the invite. You can retry from the invite link."
        );
      }
    }
  };

  return (
    <div className="min-h-screen flex relative" style={{ background: "linear-gradient(135deg, #0A0A0D 0%, #111114 50%, #0A0A0D 100%)" }}>
      <style>{`
        .mmntm-input:focus { border-color: rgba(124,58,237,0.5) !important; box-shadow: 0 0 0 1px rgba(124,58,237,0.2), 0 0 12px rgba(124,58,237,0.08); }
        .mmntm-submit:hover:not(:disabled) { box-shadow: 0 0 24px rgba(168,85,247,0.35); }
      `}</style>

      {/* Background circuit image — dark overlay, no purple wash */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG_IMAGE})` }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(10,10,13,0.93) 0%, rgba(17,17,20,0.82) 50%, rgba(10,10,13,0.93) 100%)" }} />
      </div>

      {/* Small localized purple glow — logo area only */}
      <div className="absolute pointer-events-none z-[1]"
        style={{ top: "30px", left: "40px", width: "200px", height: "200px", borderRadius: "50%", background: "radial-gradient(circle, rgba(107,33,168,0.08) 0%, transparent 70%)" }} />

      {/* Small localized purple glow — form panel only */}
      <div className="absolute pointer-events-none z-[1]"
        style={{ top: "50%", right: "8%", width: "280px", height: "280px", transform: "translateY(-50%)", borderRadius: "50%", background: "radial-gradient(circle, rgba(107,33,168,0.07) 0%, transparent 65%)" }} />

      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[48%] flex-col justify-between p-16 relative z-10">
        <div>
          <div className="flex items-center gap-3.5 mb-3">
            <MmntmLogo size={48} />
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "24px", color: "#fff", letterSpacing: "0.12em", textTransform: "uppercase" }}>MMNTM</span>
            <div style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.2)", margin: "0 4px" }} />
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "14px", color: "rgba(255,255,255,0.6)" }}>Productivity Tracker</span>
          </div>
          <div className="flex flex-col gap-1 ml-0.5">
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "11px", color: "#A855F7", letterSpacing: "0.15em", textTransform: "uppercase" }}>MOMENTUM. MASTERED.</span>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "11px", color: "#C084FC", letterSpacing: "0.15em", textTransform: "uppercase" }}>MOMENTUM REDEFINED.</span>
          </div>
        </div>

        <div className="space-y-6">
          <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: "42px", color: "#fff", lineHeight: 1.05, letterSpacing: "-0.03em" }}>
            Build MMNTM.<br />
            <span style={{ color: "#666" }}>Lock in Faster.</span>
          </h2>
          <div className="space-y-2.5">
            {[
              { label: "WORK", desc: "Enterprise-grade focus", border: "#4B6A9B" },
              { label: "TACTICAL", desc: "Gamified precision", border: "#B8892E" },
              { label: "STEALTH", desc: "Zero-distraction mode", border: "#383838" },
              { label: "GUIDED", desc: "Step-by-step, low-friction", border: "#2D8A6E" },
            ].map((m) => (
              <div key={m.label} className="flex items-center gap-3 px-4 py-2.5"
                style={{ borderLeft: `2px solid ${m.border}`, background: "rgba(255,255,255,0.015)" }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "11px", color: m.border, letterSpacing: "0.15em" }}>{m.label}</span>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "13px", color: "#777" }}>{m.desc}</span>
              </div>
            ))}
          </div>
        </div>

        <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "11px", color: "#2A2A2E", letterSpacing: "0.05em" }}>Built for people who think differently.</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 relative z-10">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col gap-2 mb-8">
            <div className="flex items-center gap-2.5">
              <MmntmLogo size={36} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "18px", color: "#fff", letterSpacing: "0.12em", textTransform: "uppercase" }}>MMNTM</span>
              <div className="hidden sm:block" style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.2)", margin: "0 4px" }} />
              <span className="hidden sm:inline-block" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>Productivity Tracker</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "10px", color: "#A855F7", letterSpacing: "0.15em", textTransform: "uppercase" }}>MOMENTUM. MASTERED.</span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "10px", color: "#C084FC", letterSpacing: "0.15em", textTransform: "uppercase" }}>MOMENTUM REDEFINED.</span>
            </div>
          </div>

          <div className="mb-8">
            <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: "28px", color: "#fff", letterSpacing: "-0.02em" }}
              data-testid="auth-heading">
              {challenge ? "Two-step check" : isLogin ? "Welcome back" : "Create account"}
            </h1>
            <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "13px", color: "#666", marginTop: "6px" }}>
              {challenge ? "One more step to confirm it's you" : isLogin ? "Sign in to your command center" : "Start building momentum"}
            </p>
          </div>

          {challenge ? (
            <TwoFactorStep onVerify={handleVerifyCode} submitting={submitting} error={error}
              onCancel={() => { setChallenge(null); setError(""); setPassword(""); }} />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div>
                <label className="block mb-1.5" style={{ fontFamily: "'Manrope', sans-serif", fontSize: "11px", fontWeight: 600, color: "#555", letterSpacing: "0.08em", textTransform: "uppercase" }}>Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm outline-none transition-all mmntm-input"
                  style={{ fontFamily: "'Manrope', sans-serif", background: "#0A0A0F", border: "1px solid #1A1A22", borderRadius: "4px", color: "#fff", caretColor: "#A855F7" }}
                  placeholder="Your name" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <label className="block mb-1.5" style={{ fontFamily: "'Manrope', sans-serif", fontSize: "11px", fontWeight: 600, color: "#555", letterSpacing: "0.08em", textTransform: "uppercase" }}>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 text-sm outline-none transition-all mmntm-input"
                style={{ fontFamily: "'Manrope', sans-serif", background: "#0A0A0F", border: "1px solid #1A1A22", borderRadius: "4px", color: "#fff", caretColor: "#A855F7" }}
                placeholder="you@example.com" required data-testid="auth-email-input" />
            </div>
            <div>
              <label className="block mb-1.5" style={{ fontFamily: "'Manrope', sans-serif", fontSize: "11px", fontWeight: 600, color: "#555", letterSpacing: "0.08em", textTransform: "uppercase" }}>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 text-sm outline-none transition-all mmntm-input"
                style={{ fontFamily: "'Manrope', sans-serif", background: "#0A0A0F", border: "1px solid #1A1A22", borderRadius: "4px", color: "#fff", caretColor: "#A855F7" }}
                placeholder={isLogin ? "Enter password" : "Min 6 characters"} required minLength={6} data-testid="auth-password-input" />
              {isLogin && (
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" className="text-xs transition-all hover:underline"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "#555" }} data-testid="auth-forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {error && (
              <div className="px-3.5 py-2.5 text-sm" style={{ fontFamily: "'Manrope', sans-serif", background: "#DC262610", color: "#F87171", border: "1px solid #DC262625", borderRadius: "4px" }}
                data-testid="auth-error">{error}</div>
            )}
            {!isLogin && (
              <EmailSuggestion hint={emailHint} submitting={submitting}
                onUse={async (s) => { setEmail(s); finishSignup(await submitSignup(s)); }}
                onKeep={async () => finishSignup(await submitSignup(email, true))} />
            )}

            {!isLogin && (
              <label className="flex items-start gap-2.5 text-xs cursor-pointer select-none"
                style={{ fontFamily: "'Manrope', sans-serif", color: "#888" }}>
                <input
                  type="checkbox"
                  checked={agreeLegal}
                  onChange={(e) => setAgreeLegal(e.target.checked)}
                  className="mt-0.5 cursor-pointer"
                  style={{ accentColor: "#A855F7" }}
                  data-testid="auth-agree-checkbox"
                />
                <span style={{ lineHeight: 1.5 }}>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: "#A855F7" }}
                    data-testid="auth-terms-link">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: "#A855F7" }}
                    data-testid="auth-privacy-link">
                    Privacy Policy
                  </Link>
                </span>
              </label>
            )}

            <button type="submit" disabled={submitting || (!isLogin && !agreeLegal)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50 mmntm-submit"
              style={{ fontFamily: "'Outfit', sans-serif", background: "#fff", color: "#000", borderRadius: "4px", letterSpacing: "-0.01em" }}
              data-testid="auth-submit-btn">
              {submitting ? "..." : isLogin ? <><LogIn className="h-3.5 w-3.5" /> Sign in</> : <><UserPlus className="h-3.5 w-3.5" /> Create account</>}
            </button>
          </form>
          )}
          {!challenge && isLogin && passkeysSupported() && (
            <button type="button" onClick={signInWithPasskey} disabled={passkeyBusy} className="w-full mt-3 py-2.5 text-sm font-semibold rounded-xl border flex items-center justify-center gap-2 transition-all hover:opacity-80 disabled:opacity-50"
              style={{ borderColor: "#333", color: "#ddd", fontFamily: "'Manrope', sans-serif" }} data-testid="auth-passkey-btn">
              <Fingerprint className="h-4 w-4" /> {passkeyBusy ? "Waiting for your device…" : "Sign in with a passkey"}
            </button>
          )}

          {!challenge && (
          <div className="text-center mt-6">
            <button onClick={() => { setIsLogin(!isLogin); setError(""); }}
              className="text-sm transition-all hover:underline"
              style={{ fontFamily: "'Manrope', sans-serif", color: "#555" }}
              data-testid="auth-toggle-btn">
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
          )}

          {/* Compact legal links — visible on login as well */}
          <div className="text-center mt-6 flex items-center justify-center gap-2"
            style={{ fontFamily: "'Manrope', sans-serif", fontSize: "11px", color: "#3a3a40" }}>
            <Link to="/terms" className="hover:underline" style={{ color: "#555" }}
              data-testid="auth-footer-terms">Terms</Link>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:underline" style={{ color: "#555" }}
              data-testid="auth-footer-privacy">Privacy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
